import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    settings: [], // Array of all settings by category
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for settings
 * used for all queries/mutations which return settings
 * @type {DocumentNode}
 */
const SETTINGS_REQUEST_FIELDS = gql`
  fragment settingsFields on Settings {
    category
    settings
    description
    updatedBy
  }
`;

/**
 * Create discriminated union input for mutations
 * @param {string} category
 * @param {*} settingsData
 */
const createPayload = (category, settingsData) => {
  if (category === 'ITEM') {
    return {
      itemSettings: {
        ...settingsData,
        hourlyRate: Number(settingsData.hourlyRate),
        margin: Number(settingsData.margin),
        generalBusinessCosts: Number(settingsData.generalBusinessCosts),
        profitMargin: Number(settingsData.profitMargin),
      },
    };
  } else if (category === 'TIME_TRACKING') {
    return {
      timeTrackingSettings: settingsData,
    };
  } else if (category === 'SALES') {
    return {
      salesSettings: {
        ...settingsData,
        totalCommission: Number(settingsData.totalCommission),
      },
    };
  }
  throw new Error(`Unknown settings category: ${category}`);
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch all settings categories
   */
  async fetchAllSettings({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query settings {
            settings {
              ...settingsFields
            }
          }
          ${SETTINGS_REQUEST_FIELDS}
        `,
        fetchPolicy: 'network-only',
      });

      commit('setSettings', response.data.settings);
      return response.data.settings;
    } catch (error) {
      console.error('Error fetching all settings:', error);
      throw new Error('Fehler beim Laden aller Einstellungen');
    }
  },

  /**
   * Fetch settings by specific category
   */
  async fetchSettingsByCategory({ commit }, category) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query settingsByCategory($category: String!) {
            settingsByCategory(category: $category) {
              ...settingsFields
            }
          }
          ${SETTINGS_REQUEST_FIELDS}
        `,
        variables: { category },
        fetchPolicy: 'network-only',
      });

      commit('updateSettings', response.data.settingsByCategory);
      return response.data.settingsByCategory;
    } catch (error) {
      console.error('Error fetching settings by category:', error);
      throw new Error('Fehler beim Laden der Einstellungen');
    }
  },

  /**
   * Update settings for a specific category
   */
  async updateSettings({ commit, dispatch }, { category, settings }) {
    try {
      const settingsInput = createPayload(category, settings);
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateSettings($category: String!, $settings: SettingsInput!) {
            updateSettings(category: $category, settings: $settings) {
              ...settingsFields
            }
          }
          ${SETTINGS_REQUEST_FIELDS}
        `,
        variables: {
          category,
          settings: settingsInput,
        },
      });

      // Update settings in global array
      commit('updateSettings', response.data.updateSettings);

      return response.data.updateSettings;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw new Error('Fehler beim Speichern der Einstellungen');
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  setSettings(state, settings) {
    state.settings = settings || [];
  },

  updateSettings(state, updatedSettings) {
    const index = state.settings.findIndex((s) => s.category === updatedSettings.category);
    if (index !== -1) {
      state.settings.splice(index, 1, updatedSettings);
    } else {
      state.settings.push(updatedSettings);
    }
  },

  resetState(state) {
    Object.assign(state, getDefaultState());
  },
};

/**
 * Getters
 */
const getters = {
  getSettings: (state) => state.settings,
  getSettingsByCategory: (state) => (category) => {
    return state.settings.find((s) => s.category === category);
  },
  getItemSettings: (state) => {
    const itemSettings = state.settings.find((s) => s.category === 'ITEM');
    return itemSettings ? itemSettings.settings : null;
  },
  getTimeTrackingSettings: (state) => {
    const timeTrackingSettings = state.settings.find((s) => s.category === 'TIME_TRACKING');
    return timeTrackingSettings ? timeTrackingSettings.settings : null;
  },
  getSalesSettings: (state) => {
    const salesSettings = state.settings.find((s) => s.category === 'SALES');
    return salesSettings ? salesSettings.settings : null;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
